/**
 * Utilitário para gerenciar o contador de mensagens não lidas no título da página
 * Implementado de forma isolada para evitar re-renderizações desnecessárias
 */

interface TabCounterConfig {
  /** Título original da página */
  originalTitle: string;
  /** Considerar mensagens de grupos no contador */
  includeGroups: boolean;
  /** Separador entre contador e título */
  separator: string;
}

class TabCounterManager {
  private config: TabCounterConfig;
  private currentCount: number = 0;
  private updateTimer: NodeJS.Timeout | null = null;
  private readonly DEBOUNCE_DELAY = 1000; // 1 segundo para evitar atualizações frequentes

  constructor(config: Partial<TabCounterConfig> = {}) {
    this.config = {
      originalTitle: document.title || 'SinTicket',
      includeGroups: false, // Por padrão não considera grupos
      separator: ' - ',
      ...config
    };
  }

  /**
   * Atualiza o contador de mensagens não lidas no título
   * Implementado com debounce para evitar múltiplas atualizações rápidas
   */
  updateCounter(count: number): void {
    // Limpar timer anterior se existir
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
    }

    // Debounce para evitar atualizações muito frequentes
    this.updateTimer = setTimeout(() => {
      this.performUpdate(count);
    }, this.DEBOUNCE_DELAY);
  }

  /**
   * Realiza a atualização efetiva do título
   */
  private performUpdate(count: number): void {
    this.currentCount = count;

    try {
      if (count > 0) {
        // Adicionar contador ao título
        document.title = `(${count})${this.config.separator}${this.config.originalTitle}`;
      } else {
        // Manter o título original sem contador quando não há mensagens não lidas
        document.title = this.config.originalTitle;
      }
    } catch (error) {
      console.warn('Erro ao atualizar título da página:', error);
    }
  }

  /**
   * Atualiza a configuração do contador
   */
  updateConfig(newConfig: Partial<TabCounterConfig>): void {
    this.config = { ...this.config, ...newConfig };

    // Reaplicar contador atual com nova configuração
    this.performUpdate(this.currentCount);
  }

  /**
   * Obtém o contador atual
   */
  getCurrentCount(): number {
    return this.currentCount;
  }

  /**
   * Limpa recursos ao destruir o gerenciador
   */
  destroy(): void {
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
      this.updateTimer = null;
    }

    // Restaurar título original
    try {
      document.title = this.config.originalTitle;
    } catch (error) {
      console.warn('Erro ao restaurar título original:', error);
    }
  }

  /**
   * Força atualização imediata (sem debounce)
   */
  forceUpdate(count: number): void {
    if (this.updateTimer) {
      clearTimeout(this.updateTimer);
      this.updateTimer = null;
    }

    this.performUpdate(count);
  }
}

// Instância global para uso em toda a aplicação
let globalTabCounter: TabCounterManager | null = null;

/**
 * Inicializa o contador global de mensagens não lidas
 */
export function initializeTabCounter(config?: Partial<TabCounterConfig>): TabCounterManager {
  if (!globalTabCounter) {
    globalTabCounter = new TabCounterManager(config);
  }
  return globalTabCounter;
}

/**
 * Obtém a instância global do contador
 */
export function getTabCounter(): TabCounterManager | null {
  return globalTabCounter;
}

/**
 * Destroi o contador global
 */
export function destroyTabCounter(): void {
  if (globalTabCounter) {
    globalTabCounter.destroy();
    globalTabCounter = null;
  }
}

export { TabCounterManager };
export default TabCounterManager;
